import { CheckIcon } from "@heroicons/react/20/solid";
import {
  ArrowRightIcon,
  BookmarkSquareIcon,
  ClockIcon,
  CubeIcon,
  QrCodeIcon,
  ShieldCheckIcon,
  SparklesIcon,
} from "@heroicons/react/24/outline";
import { motion } from "motion/react";
import { useEffect } from "react";
import { useIntl } from "react-intl";
import { useHistory, useParams } from "react-router";
import { Link } from "react-router-dom";
import CardsSvg, { VisaSvg } from "../components/CardsSvg";
import { IpadMockImage, IphoneMockImage } from "../components/DeviceMocks";
import BottomCallToAction from "../components/home/BottomCallToAction";
import { LazyPicture } from "../components/Picture";
import { FeatureGrid } from "../components/reusable/FeatureGrid";
import { ServiceCard } from "../components/reusable/ServiceCard";
import { Button } from "../components/ui/button";
import { useAppSelector } from "../state/hooks";
import Faq from "./Faq";
import TestemonialsBlock from "./TestemonialsBlock";

// Removed - replaced with reusable FeatureCard component

const MainFeatures = () => {
  const features = [
    {
      id: "lightning-fast",
      icon: ClockIcon,
      title: "Lightning Fast",
      description: "Process orders, payments, and reservations with unmatched speed and efficiency that keeps your service flowing smoothly.",
    },
    {
      id: "intelligent-insights",
      icon: SparklesIcon,
      title: "Intelligent Insights",
      description: "Advanced analytics and AI-powered recommendations help you make data-driven decisions to optimize performance.",
    },
    {
      id: "enterprise-security",
      icon: ShieldCheckIcon,
      title: "Enterprise Security",
      description: "Bank-level security, PCI compliance, and 99.9% uptime ensure your restaurant data is always protected and accessible.",
    },
  ];

  return (
    <FeatureGrid
      badge={{ icon: SparklesIcon, text: "Built for excellence" }}
      title="Modern restaurant"
      subtitle="management platform"
      description="Everything you need to streamline operations, enhance customer experience, and grow your restaurant business with confidence."
      features={features}
      columns={3}
    />
  );
};

function IPadShape({ ...props }) {
  const isDark = useAppSelector((state) => state.system.theme === "dark");

  const srcset = !isDark
    ? {
        type: "image/png",
        src: "https://static.tableport.io/il6np73l5000109ml5n9036fx.png",
        webp: "https://static.tableport.io/il6np73l5000109ml5n9036fx.webp",
      }
    : {
        type: "image/png",
        src: "https://static.tableport.io/il6np23wt000009mlh3ikf97g.png",
        webp: "https://static.tableport.io/il6np23wt000009mlh3ikf97g.webp",
      };

  return (
    <LazyPicture
      offset={800}
      {...props}
      alt="pos"
      srcset={srcset}
      height="auto"
      width="80%"
    />
  );
}

const BULLET_POINTS = [
  "Free trial, automated onboarding",
  "No setup fees or hidden costs",
  "Cancel anytime with no penalties",
];

const VisaInnovation = () => {
  return (
    <motion.div
      className="flex items-center justify-center"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ delay: 1, duration: 0.8 }}
    >
      <div className="flex flex-row w-fit items-center px-2 rounded-full bg-white/20 dark:bg-gray-800/20 backdrop-blur-sm border border-gray-300/30 dark:border-gray-600/30">
        <span className="text-xs font-bold text-gray-600 dark:text-gray-400">
          Members of
        </span>
        <VisaSvg width={50} height={32} />
        <span className="text-xs font-bold text-gray-600 dark:text-gray-400">
          Innovation Studio
        </span>
      </div>
    </motion.div>
  );
};

const HomeHero = () => {
  const isDark = useAppSelector((state) => state.system.theme === "dark");

  return (
    <section 
      className="relative min-h-[90vh] flex items-center overflow-hidden"
      aria-labelledby="hero-heading"
    >
      {/* Simplified static background for better performance */}
      <div className="absolute inset-0 bg-gradient-to-br from-slate-50 via-white to-gray-50 dark:from-gray-900 dark:via-gray-950 dark:to-gray-900" />
      
      {/* Single optimized overlay */}
      <div className="absolute inset-0 bg-gradient-to-b from-white/40 via-white/20 to-white/80 dark:from-gray-900/40 dark:via-gray-900/20 dark:to-gray-900/80" />

      <div className="relative z-10 mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-20">
        <div className="text-center space-y-10">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, ease: "easeOut" }}
          >
            <h1 
              id="hero-heading"
              className="max-w-4xl mx-auto text-3xl sm:text-5xl md:text-6xl lg:text-7xl font-bold text-gray-900 dark:text-white tracking-tight leading-tight"
            >
              The platform that drives successful restaurants
            </h1>
          </motion.div>

          <motion.p
            className="text-lg sm:text-xl md:text-2xl text-gray-800 dark:text-gray-100 max-w-4xl mx-auto leading-relaxed font-medium"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.1, ease: "easeOut" }}
          >
            Streamline your entire restaurant operation with our truly
            all-in-one system. From orders, payments, reservations, and
            analytics, we've got you covered.
          </motion.p>

          <motion.div
            className="flex flex-col sm:flex-row sm:flex-wrap items-center justify-center gap-4 sm:gap-8 py-6"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2, ease: "easeOut" }}
            role="list"
            aria-label="Key benefits"
          >
            {BULLET_POINTS.map((point, i) => (
              <div
                className="flex items-center gap-3"
                key={i}
                role="listitem"
              >
                <div 
                  className="w-5 h-5 rounded-full bg-green-100 dark:bg-green-900/30 flex items-center justify-center border border-green-300 dark:border-green-600 shadow-sm"
                  aria-hidden="true"
                >
                  <CheckIcon className="w-3 h-3 text-green-600 dark:text-green-400" />
                </div>
                <span className="text-gray-800 dark:text-gray-100 font-medium text-sm">
                  {point}
                </span>
              </div>
            ))}
          </motion.div>

          <motion.div
            className="flex flex-col sm:flex-row items-center justify-center gap-4 sm:gap-6"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.3, ease: "easeOut" }}
          >
            <Button
              asChild
              size="xl"
              className="group hover:shadow-xl hover:-translate-y-0.5 active:translate-y-0 active:shadow-md transition-all duration-200 ease-out"
              aria-label="Start your free trial"
            >
              <Link to="/join">
                Start Free Trial
                <ArrowRightIcon className="w-4 h-4 group-hover:translate-x-0.5 transition-transform duration-200" aria-hidden="true" />
              </Link>
            </Button>

            <Button
              asChild
              variant="outline"
              size="xl"
              className="bg-white/80 dark:bg-gray-800/20 backdrop-blur-sm 
                         border-gray-400/50 dark:border-gray-600/30 
                         hover:bg-white dark:hover:bg-gray-800/40 hover:border-gray-500/70 dark:hover:border-gray-500/40
                         hover:-translate-y-0.5 hover:shadow-lg
                         active:translate-y-0 active:shadow-sm
                         transition-all duration-200 ease-out"
              aria-label="Schedule a demo"
            >
              <Link to="/pages/contact">
                Schedule Demo
              </Link>
            </Button>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

// Removed - replaced with reusable ServiceCard component

const ExplainsBlock = () => {
  const intl = useIntl();
  return (
    <motion.div
      className="rounded-2xl border border-gray-200/60 dark:border-gray-700/60 bg-gradient-to-br from-white via-gray-50/50 to-slate-50/50 dark:from-gray-800 dark:via-gray-800 dark:to-gray-700 overflow-hidden shadow-lg backdrop-blur-sm"
      initial={{ opacity: 0, y: 40 }}
      whileInView={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.8 }}
      viewport={{ once: true }}
    >
      <div className="flex flex-col lg:flex-row items-center justify-center">
        <motion.div
          className="flex flex-1 lg:flex-2 items-center justify-center p-4 sm:p-8"
          initial={{ opacity: 0, x: -30 }}
          whileInView={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.8, delay: 0.2 }}
          viewport={{ once: true }}
        >
          <IpadMockImage
            image={{
              src: "https://static.tableport.io/images/iltebsi4a000008k198tm0l0g.png",
              webp: "https://static.tableport.io/images/iltebsi4a000008k198tm0l0g.webp",
              type: "image/png",
            }}
            className="transform scale-75 sm:scale-90 drop-shadow-lg"
          />
        </motion.div>
        <motion.div
          className="flex flex-1 lg:flex-2 flex-col p-6 sm:p-12 space-y-4 sm:space-y-6"
          initial={{ opacity: 0, x: 30 }}
          whileInView={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.8, delay: 0.4 }}
          viewport={{ once: true }}
        >
          <span className="inline-flex items-center px-3 py-1.5 rounded-full text-xs font-medium bg-slate-100 dark:bg-slate-800 text-slate-700 dark:text-slate-300 w-fit">
            <SparklesIcon className="w-3 h-3 mr-1.5" />
            Real-time insights
          </span>
          <h3 className="text-2xl sm:text-3xl font-bold text-gray-900 dark:text-gray-100 tracking-tight">
            Data that drives
            <span className="block text-slate-600 dark:text-slate-400">
              better decisions
            </span>
          </h3>
          <div className="text-gray-600 dark:text-gray-400 leading-relaxed space-y-4">
            <p>
              Get instant visibility into every aspect of your restaurant with
              our comprehensive analytics dashboard.
            </p>
            <p className="font-medium text-gray-900 dark:text-gray-100">
              Track sales, and optimize staff performance with real-time data
              that helps you make smarter business decisions.
            </p>
          </div>
        </motion.div>
      </div>
    </motion.div>
  );
};

const CollectPaymentsBlock = () => {
  return (
    <motion.div
      className="rounded-2xl bg-gradient-to-br from-slate-50 via-gray-50 to-slate-100 dark:from-gray-800 dark:via-gray-800 dark:to-gray-700 p-6 sm:p-12 lg:p-16 text-center shadow-lg border border-slate-200/60 dark:border-gray-600/60"
      initial={{ opacity: 0, y: 40 }}
      whileInView={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.8 }}
      viewport={{ once: true }}
    >
      <div className="w-full mx-auto">
        <div className="space-y-6 sm:space-y-8 mb-8 sm:mb-12">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
          >
            <span className="inline-flex items-center px-3 py-1.5 rounded-full text-xs font-medium bg-slate-100 dark:bg-slate-800 text-slate-700 dark:text-slate-300">
              <SparklesIcon className="w-3 h-3 mr-1.5" />
              Lightning fast payments
            </span>
          </motion.div>
          <motion.h3
            className="text-2xl sm:text-3xl md:text-4xl font-bold text-gray-900 dark:text-gray-100 tracking-tight"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.1 }}
            viewport={{ once: true }}
          >
            Accept payments
            <span className="block text-slate-600 dark:text-slate-400">
              faster than ever
            </span>
          </motion.h3>
          <motion.p
            className="text-base sm:text-lg text-gray-600 dark:text-gray-400 max-w-2xl mx-auto leading-relaxed"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
            viewport={{ once: true }}
          >
            Support all payment methods with secure, PCI-compliant processing.
            From contactless cards to digital wallets, we've got you covered.
          </motion.p>
        </div>
        <motion.div
          className="max-w-2xl mx-auto"
          initial={{ opacity: 0, scale: 0.9 }}
          whileInView={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.8, delay: 0.3 }}
          viewport={{ once: true }}
        >
          <CardsSvg />
        </motion.div>
      </div>
    </motion.div>
  );
};

const ServicesBlocks = () => {
  return (
    <motion.div
      className="space-y-12 lg:space-y-16"
      initial={{ opacity: 0 }}
      whileInView={{ opacity: 1 }}
      transition={{ duration: 0.8 }}
      viewport={{ once: true }}
    >
      <div className="text-center space-y-4 sm:space-y-6">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          viewport={{ once: true }}
        >
          <span className="inline-flex items-center px-3 py-1.5 rounded-full text-xs font-medium bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-300 border border-gray-200/50 dark:border-gray-700/50">
            <CubeIcon className="w-3 h-3 mr-1.5" />
            Complete platform
          </span>
        </motion.div>
        <motion.h2
          className="text-2xl sm:text-3xl md:text-4xl font-bold text-gray-900 dark:text-gray-100 tracking-tight"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.1 }}
          viewport={{ once: true }}
        >
          Everything your restaurant
          <span className="block text-slate-600 dark:text-slate-400">
            needs to succeed
          </span>
        </motion.h2>
        <motion.p
          className="text-base sm:text-lg text-gray-600 dark:text-gray-400 max-w-2xl mx-auto leading-relaxed"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.2 }}
          viewport={{ once: true }}
        >
          From point-of-sale to reservations, streamline every aspect of your
          restaurant operation with our integrated platform.
        </motion.p>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8">
        <ServiceCard
          icon={CubeIcon}
          description="Modern point-of-sale system with intuitive design, and seamless payment processing."
          href="/pages/pos"
          title="Point of Sale"
          image={{
            src: "https://static.tableport.io/images/ilteb0ht2000008l2a1mngisr.png",
            webp: "https://static.tableport.io/images/ilteb0ht2000008l2a1mngisr.webp",
            type: "image/png",
          }}
        />
        <ServiceCard
          icon={QrCodeIcon}
          description="Enable customers to order and pay directly from their table with QR codes, reducing wait times and increasing satisfaction."
          href="/pages/order-and-pay"
          title="Order & Pay"
          image={{
            src: "https://static.tableport.io/images/il82teu3a000109mlhlj0557l.png",
            webp: "https://static.tableport.io/images/il82teu3a000109mlhlj0557l.webp",
            type: "image/png",
          }}
        />
        <ServiceCard
          icon={BookmarkSquareIcon}
          description="Smart reservation management with automated confirmations and optimized table allocation."
          href="/pages/reservations"
          title="Reservations"
          image={{
            src: "/reservations-screenshot-iphone.png",
            webp: "/reservations-screenshot-iphone.webp",
            type: "image/png",
          }}
        />
      </div>
    </motion.div>
  );
};

const CountryPath = () => {
  const params = useParams<{ country: string }>();
  const history = useHistory();

  const ready = useAppSelector((s) => s._persist.rehydrated);
  const country = useAppSelector((s) => s.system.country);

  useEffect(() => {
    if (ready && params.country) {
      if (params.country !== country) {
        history.push("/" + country?.toLowerCase?.());
      }
    }
  }, [params.country, country, ready, history]);

  return null;
};

const HomeScreen = () => {
  return (
    <main className="flex flex-col">
      <CountryPath />
      <HomeHero />
      <div
        className="flex shrink flex-col gap-y-12 sm:gap-y-16 lg:gap-y-20 py-12 sm:py-16 lg:py-20 max-w-7xl w-full mx-auto px-4 sm:px-6 lg:px-8"
      >
        <ServicesBlocks />
        <CollectPaymentsBlock />
        <ExplainsBlock />
        <MainFeatures />
        <TestemonialsBlock />
        <Faq />
        <BottomCallToAction />
      </div>
    </main>
  );
};

export default HomeScreen;
